<template>
  <BillingSkeletonLoader v-if="loading" />
  <div v-else class="proxima_nova">
    <div id="manage-billing-v4" class="flex flex-col gap-10 max-w-[1100px] mx-auto px-4 py-6 pb-28">
      <!-- Section 1: Add One Time Credits -->
      <div class="flex flex-col gap-5">
        <div class="flex flex-row items-baseline justify-between">
          <span class="text-[28px] font-bold text_color">Add One Time Credits</span>
          <span v-if="availableCredits !== null" class="text-base secondary_color">
            Available Credits: <span class="font-semibold text_color">{{ availableCredits.toLocaleString() }}</span>
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm secondary_color">How many credits do you want</label>
          <div class="v4_input_wrapper">
            <input v-model="creditCountDisplay" type="text" class="v4_input" placeholder="Enter credit amount" @input="handleCreditInput" />
            <span class="v4_input_suffix">credits</span>
          </div>
        </div>

        <span v-if="belowMinimum" class="text-sm text-red-500 -mt-1">A minimum of 500 credits can be purchased</span>

        <!-- Quick Select Chips -->
        <div class="flex flex-row gap-2 flex-wrap">
          <span
            v-for="option in QUICK_SELECT_OPTIONS"
            :key="'onetime-' + option"
            :class="['credit_chip text-sm px-4 py-[6px] rounded-3xl cursor-pointer transition-all', creditCount === option ? 'credit_chip_active font-semibold' : 'hover:opacity-80']"
            @click="selectCreditOption(option)"
          >
            {{ option.toLocaleString() }} credits
          </span>
        </div>

        <!-- Price + Buy Button -->
        <div class="flex flex-row items-end justify-between">
          <div class="flex flex-col gap-1">
            <span class="text-sm secondary_color">Price</span>
            <span class="text-[28px] font-bold text_color">{{ paymentGateway === 'razorpay' ? '₹' : '$' }}{{ calculatedPrice }}</span>
          </div>
          <a-button
            class="buy_credits_btn !rounded-xl !px-6 !py-2 !h-auto !text-sm !font-semibold"
            :loading="isProcessingOneTime"
            :disabled="creditCount <= 0 || isProcessingOneTime || belowMinimum"
            @click="handleAddCredits"
          >
            Add credits
          </a-button>
        </div>
      </div>

      <!-- Divider -->
      <div class="section_divider"></div>

      <!-- Section 2: Auto Recharge (hidden for Razorpay users) -->
      <div class="flex flex-col gap-5">
        <span class="text-[28px] font-bold text_color">Auto Recharge</span>

        <div class="flex flex-col gap-2">
          <label class="text-sm secondary_color">How many credits do you want to be added every month</label>
          <div class="v4_input_wrapper">
            <input v-model="monthlyCreditCountDisplay" type="text" class="v4_input" placeholder="Enter credit amount" @input="handleMonthlyCreditInput" />
            <span class="v4_input_suffix">credits/month</span>
          </div>
        </div>

        <span v-if="belowMonthlyMinimum" class="text-sm text-red-500 -mt-1">A minimum of 7500 credits can be set for auto recharge</span>

        <!-- Quick Select Chips (Monthly) -->
        <div class="flex flex-row gap-2 flex-wrap">
          <span
            v-for="option in QUICK_SELECT_OPTIONS"
            :key="'monthly-' + option"
            :class="['credit_chip text-sm px-4 py-[6px] rounded-3xl cursor-pointer transition-all', monthlyCreditCount === option ? 'credit_chip_active font-semibold' : 'hover:opacity-80']"
            @click="selectMonthlyOption(option)"
          >
            {{ option.toLocaleString() }} credits
          </span>
        </div>

        <!-- Monthly Price + Action Buttons -->
        <div class="flex flex-row items-end justify-between">
          <div class="flex flex-col gap-1">
            <span class="text-sm secondary_color">Price</span>
            <span class="text-[28px] font-bold text_color">
              {{ paymentGateway === 'razorpay' ? '₹' : '$' }}{{ calculatedMonthlyPrice }}
              <span class="text-base font-medium secondary_color">every month</span>
            </span>
          </div>

          <!-- No active subscription: Setup -->
          <a-button
            v-if="!hasActiveAutoRecharge"
            class="buy_credits_btn !rounded-xl !px-6 !py-2 !h-auto !text-sm !font-semibold"
            :loading="isProcessingMonthly"
            :disabled="monthlyCreditCount <= 0 || isProcessingMonthly || belowMonthlyMinimum"
            @click="handleSetupAutoRecharge"
          >
            Setup Auto Recharge
          </a-button>

          <!-- Active subscription, credit count matches: Cancel -->
          <a-button
            v-else-if="monthlyCreditCount === activeRechargeCreditCount"
            class="cancel_subscription_btn !rounded-xl !px-6 !py-2 !h-auto !text-sm !font-semibold"
            @click="showCancelModal = true"
          >
            Cancel subscription
          </a-button>

          <!-- Active subscription, credit count changed: Save + Cancel changes -->
          <div v-else class="flex flex-row gap-2">
            <a-button
              class="save_changes_btn !rounded-xl !px-6 !py-2 !h-auto !text-sm !font-semibold"
              :loading="isProcessingMonthly"
              :disabled="monthlyCreditCount <= 0 || isProcessingMonthly || belowMonthlyMinimum"
              @click="handleSaveChanges"
            >
              Save changes
            </a-button>
            <a-button class="cancel_changes_btn !rounded-xl !px-6 !py-2 !h-auto !text-sm !font-semibold" :disabled="isProcessingMonthly" @click="handleCancelChanges"> Cancel changes </a-button>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="section_divider"></div>

      <!-- Section 3: AI Credit Consumption Breakdown -->
      <AICreditBreakdown />

      <!-- Section 4: What Are Credits -->
      <WhatAreCreditsComponent />

      <!-- Company logos -->
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Trusted by forwarding businesses of all sizes</span>
        <div class="flex flex-row gap-8 items-center w-full">
          <img :src="companyLogoSrc" alt="Company logos" class="w-full object-contain" />
        </div>
      </div>

      <!-- Customer reviews -->
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Real Customers, Real Impact</span>
        <div class="flex flex-row gap-6">
          <CustomReviewComponent
            v-for="(review, idx) in customerReviews"
            :key="idx"
            :review="review.review"
            :name="review.name"
            :userName="review.userName"
            :designation="review.designation"
            :avatar="review.avatar"
          />
        </div>
      </div>

      <!-- FAQ -->
      <div class="flex flex-col gap-6 w-full">
        <span class="text-[24px] font-semibold text_color text-center">Know Your Billing</span>
        <BillingFAQAccordion :faqList="billingFAQv4" />
      </div>
    </div>

    <!-- Stripe Checkout Modal -->
    <StripeCheckoutModal v-model:visible="showStripeCheckout" :client-secret="stripeClientSecret" :modal-title="checkoutModalTitle" @success="handleCheckoutSuccess" @error="handleCheckoutError" />

    <!-- Razorpay Checkout Modal -->
    <RazorpayCheckoutModal v-model:visible="showRazorpayCheckout" :order-data="razorpayOrderData" @success="handleRazorpaySuccess" @error="handleRazorpayError" />

    <!-- Cancel Auto Recharge Confirmation Modal -->
    <ConfirmationModal
      v-model:visible="showCancelModal"
      title="Cancel Auto Recharge"
      message="Are you sure you want to cancel your monthly auto-recharge subscription? Your existing credits will remain available."
      type="danger"
      confirm-text="Confirm Cancellation"
      cancel-text="Cancel"
      loading-text="Cancelling..."
      :loading="isCancelling"
      @confirm="handleConfirmCancel"
      @cancel="showCancelModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import DashboardServiceV4 from '@/services/DashboardServiceV4';
import WhatAreCreditsComponent from '@/components/manage_billing_components/WhatAreCreditsComponent.vue';
import AICreditBreakdown from '@/components/manage_billing_components/AICreditBreakdown.vue';
import CustomReviewComponent from '@/components/manage_billing_components/CustomReviewComponent.vue';
import BillingFAQAccordion from '@/components/manage_billing_components/BillingFAQAccordion.vue';
import StripeCheckoutModal from '@/components/manage_billing_components/StripeCheckoutModal.vue';
import RazorpayCheckoutModal from '@/components/manage_billing_components/RazorpayCheckoutModal.vue';
import BillingSkeletonLoader from '@/components/custom/BillingSkeletonLoader.vue';
import ConfirmationModal from '@/components/custom/ConfirmationModal.vue';
import companyLogoSrc from '@/assets/images/company_logos.svg';
import { customerReviews, billingFAQv4 } from '@/constants/billingConstants';
import { message } from 'ant-design-vue';

const QUICK_SELECT_OPTIONS = [10000, 25000, 50000, 200000];

// State
const loading = ref(true);
const creditCount = ref(2500);
const creditCountDisplay = ref('2,500');
const monthlyCreditCount = ref(20000);
const monthlyCreditCountDisplay = ref('20,000');
const creditPriceRate = ref(0);
const billingAccount = ref(null);

// Stripe checkout state
const showStripeCheckout = ref(false);
const stripeClientSecret = ref('');
const checkoutModalTitle = ref('Complete Your Payment');
const isProcessingOneTime = ref(false);
const isProcessingMonthly = ref(false);

// Auto-recharge state
const hasActiveAutoRecharge = ref(false);
const activeRechargeCreditCount = ref(0);
const autoRechargePaymentMethod = ref('');
const showCancelModal = ref(false);
const isCancelling = ref(false);
const pendingSubscriptionUpdate = ref(null);

// Razorpay state
const paymentGateway = ref('stripe');
const creditPriceRateINR = ref(0);
const showRazorpayCheckout = ref(false);
const razorpayOrderData = ref(null);

// Format number with commas
const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Handle credit input with formatting
const handleCreditInput = (e) => {
  const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
  const num = Number(raw) || 0;
  creditCount.value = num;
  creditCountDisplay.value = num > 0 ? formatNumber(num) : '';
};

const handleMonthlyCreditInput = (e) => {
  const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
  const num = Number(raw) || 0;
  monthlyCreditCount.value = num;
  monthlyCreditCountDisplay.value = num > 0 ? formatNumber(num) : '';
};

const selectCreditOption = (value) => {
  creditCount.value = value;
  creditCountDisplay.value = formatNumber(value);
};

const selectMonthlyOption = (value) => {
  monthlyCreditCount.value = value;
  monthlyCreditCountDisplay.value = formatNumber(value);
};

// Computed prices
const calculatedPrice = computed(() => {
  const count = creditCount.value || 0;
  const rate = paymentGateway.value === 'razorpay' ? creditPriceRateINR.value : creditPriceRate.value;
  return Math.ceil(count * rate);
});

const belowMinimum = computed(() => {
  return creditCount.value > 0 && creditCount.value < 500;
});

const belowMonthlyMinimum = computed(() => {
  return monthlyCreditCount.value > 0 && monthlyCreditCount.value < 7500;
});

const calculatedMonthlyPrice = computed(() => {
  const count = monthlyCreditCount.value || 0;
  const rate = paymentGateway.value === 'razorpay' ? creditPriceRateINR.value : creditPriceRate.value;
  return Math.ceil(count * rate);
});

const availableCredits = computed(() => {
  if (!billingAccount.value) return null;
  const credits = billingAccount.value.purchased_agent_credits ?? billingAccount.value.PurchasedAgentCredits ?? 0;
  return Math.floor(credits);
});

// Fetch account data on mount
onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isSuccess = urlParams.get('success') === 'true';
  const wasRecurring = urlParams.get('recurring') === 'true';
  const wasUpgrade = urlParams.get('upgrade') === 'true';
  const recurringCredits = Number(urlParams.get('credits')) || 0;

  if (isSuccess) {
    window.history.replaceState({}, '', window.location.pathname);

    if (wasUpgrade && recurringCredits > 0) {
      // Redirect return from upgrade checkout — update the subscription
      try {
        const updateResponse = await DashboardServiceV4.updateAutoRecharge(recurringCredits);
        if (updateResponse?.success) {
          hasActiveAutoRecharge.value = true;
          monthlyCreditCount.value = recurringCredits;
          activeRechargeCreditCount.value = recurringCredits;
          monthlyCreditCountDisplay.value = formatNumber(recurringCredits);
          message.success('Payment successful! Your subscription has been updated.');
        } else {
          message.error(updateResponse?.error || 'Payment successful, but subscription update failed. Please try saving again.');
        }
      } catch (err) {
        console.error('Error updating subscription after redirect:', err);
        message.error('Payment successful, but subscription update failed. Please try saving again.');
      }
    } else if (wasRecurring && recurringCredits > 0) {
      hasActiveAutoRecharge.value = true;
      monthlyCreditCount.value = recurringCredits;
      activeRechargeCreditCount.value = recurringCredits;
      monthlyCreditCountDisplay.value = formatNumber(recurringCredits);
      message.success('Payment successful! Your credits have been added.');
    } else {
      message.success('Payment successful! Your credits have been added.');
    }
  }

  try {
    const response = await DashboardServiceV4.getOrCreateAccount();
    if (response) {
      creditPriceRate.value = response.credit_price_rate || 0;
      creditPriceRateINR.value = response.credit_price_rate_inr || 0;
      if (response.payment_gateway) {
        paymentGateway.value = response.payment_gateway;
        if (response.payment_gateway === 'razorpay') {
          creditCount.value = 5000;
          creditCountDisplay.value = '5,000';
        }
      }
      if (response.success) {
        billingAccount.value = response.account;
      }
      if (response.auto_recharge_enabled) {
        hasActiveAutoRecharge.value = true;
        monthlyCreditCount.value = response.auto_recharge_credit_count || 20000;
        activeRechargeCreditCount.value = monthlyCreditCount.value;
        monthlyCreditCountDisplay.value = formatNumber(monthlyCreditCount.value);
        autoRechargePaymentMethod.value = response.auto_recharge_payment_method || '';
      } else if (wasRecurring && recurringCredits > 0) {
        hasActiveAutoRecharge.value = true;
        monthlyCreditCount.value = recurringCredits;
        activeRechargeCreditCount.value = recurringCredits;
        monthlyCreditCountDisplay.value = formatNumber(recurringCredits);
      }
    }
  } catch (error) {
    console.error('Error fetching billing account:', error);
  } finally {
    loading.value = false;
  }
});

// One-time credit purchase
const handleAddCredits = async () => {
  if (creditCount.value <= 0) {
    message.warning('Please enter a credit amount');
    return;
  }
  isProcessingOneTime.value = true;
  try {
    const returnUrl = `${window.location.origin}/manage_billing?success=true`;
    const response = await DashboardServiceV4.addCreditsOneTime(creditCount.value, 'embedded', returnUrl);

    if (response?.success) {
      if (response.payment_gateway === 'razorpay') {
        // Razorpay flow: show Razorpay checkout modal
        razorpayOrderData.value = response;
        showRazorpayCheckout.value = true;
        return;
      }
      // Stripe flow
      const clientSecret = response.client_secret;
      if (clientSecret) {
        stripeClientSecret.value = clientSecret;
        checkoutModalTitle.value = 'Add Credits';
        showStripeCheckout.value = true;
      } else if (response?.checkout_url) {
        window.location.href = response.checkout_url;
      }
    } else {
      message.error(response?.error || 'Failed to create checkout session');
      isProcessingOneTime.value = false;
    }
  } catch (error) {
    console.error('Error creating one-time checkout:', error);
    message.error('Error creating checkout session');
    isProcessingOneTime.value = false;
  }
};

// Setup auto-recharge (no active subscription)
const handleSetupAutoRecharge = async () => {
  if (monthlyCreditCount.value <= 0) {
    message.warning('Please enter a monthly credit amount');
    return;
  }

  isProcessingMonthly.value = true;
  try {
    const returnUrl = `${window.location.origin}/manage_billing?success=true&recurring=true&credits=${monthlyCreditCount.value}`;
    const response = await DashboardServiceV4.buyCredits(monthlyCreditCount.value, 'embedded', returnUrl, true);
    if (response?.success) {
      if (response.payment_gateway === 'razorpay') {
        // Razorpay subscription flow
        razorpayOrderData.value = { ...response, type: 'subscription' };
        showRazorpayCheckout.value = true;
        // isProcessingMonthly stays true until subscription activated or cancelled
      } else {
        const clientSecret = response.client_secret;
        if (clientSecret) {
          stripeClientSecret.value = clientSecret;
          checkoutModalTitle.value = 'Monthly Credit Subscription';
          showStripeCheckout.value = true;
        } else if (response?.checkout_url) {
          window.location.href = response.checkout_url;
        }
        isProcessingMonthly.value = false;
      }
    } else {
      message.error(response?.error || 'Failed to create checkout session');
      isProcessingMonthly.value = false;
    }
  } catch (error) {
    console.error('Error setting up auto-recharge:', error);
    message.error('Error setting up auto-recharge');
    isProcessingMonthly.value = false;
  }
};

// Update existing subscription (credit count changed)
const handleSaveChanges = async () => {
  if (monthlyCreditCount.value <= 0) {
    message.warning('Please enter a monthly credit amount');
    return;
  }

  if (autoRechargePaymentMethod.value === 'upi') {
    message.error('UPI subscriptions cannot be updated. Please cancel your current auto-recharge and set it up again with the new credit amount.');
    return;
  }

  const delta = monthlyCreditCount.value - activeRechargeCreditCount.value;

  isProcessingMonthly.value = true;
  try {
    if (delta > 0) {
      // Increasing: charge for delta credits now, then update subscription after payment
      pendingSubscriptionUpdate.value = monthlyCreditCount.value;
      const returnUrl = `${window.location.origin}/manage_billing?success=true&upgrade=true&credits=${monthlyCreditCount.value}`;
      const response = await DashboardServiceV4.addCreditsOneTime(delta, 'embedded', returnUrl);
      if (response?.success) {
        if (response.payment_gateway === 'razorpay') {
          razorpayOrderData.value = { ...response, type: 'order' };
          showRazorpayCheckout.value = true;
          // isProcessingMonthly stays true until payment completes or is cancelled
        } else {
          const clientSecret = response.client_secret;
          if (clientSecret) {
            stripeClientSecret.value = clientSecret;
            checkoutModalTitle.value = 'Pay for Additional Credits';
            showStripeCheckout.value = true;
          } else if (response?.checkout_url) {
            window.location.href = response.checkout_url;
          }
        }
      } else {
        message.error(response?.error || 'Failed to create checkout session');
        pendingSubscriptionUpdate.value = null;
        isProcessingMonthly.value = false;
      }
    } else {
      // Decreasing: just update the subscription (takes effect next billing cycle)
      const response = await DashboardServiceV4.updateAutoRecharge(monthlyCreditCount.value);
      if (response?.success) {
        activeRechargeCreditCount.value = monthlyCreditCount.value;
        message.success('Auto-recharge updated successfully.');
      } else {
        message.error(response?.error || 'Failed to update auto-recharge');
      }
      isProcessingMonthly.value = false;
    }
  } catch (error) {
    console.error('Error saving auto-recharge:', error);
    message.error('Error saving auto-recharge');
    pendingSubscriptionUpdate.value = null;
    isProcessingMonthly.value = false;
  }
};

// Revert input to active subscription's credit count
const handleCancelChanges = () => {
  monthlyCreditCount.value = activeRechargeCreditCount.value;
  monthlyCreditCountDisplay.value = formatNumber(activeRechargeCreditCount.value);
};

// Stripe checkout handlers
const handleCheckoutSuccess = async () => {
  showStripeCheckout.value = false;
  const wasRecurring = isProcessingMonthly.value;
  const subscriptionUpdateCredits = pendingSubscriptionUpdate.value;
  pendingSubscriptionUpdate.value = null;

  try {
    if (subscriptionUpdateCredits) {
      // Upgrade checkout completed — now update the subscription
      const updateResponse = await DashboardServiceV4.updateAutoRecharge(subscriptionUpdateCredits);
      if (updateResponse?.success) {
        hasActiveAutoRecharge.value = true;
        activeRechargeCreditCount.value = subscriptionUpdateCredits;
        monthlyCreditCount.value = subscriptionUpdateCredits;
        monthlyCreditCountDisplay.value = formatNumber(subscriptionUpdateCredits);
        message.success('Payment successful! Your subscription has been updated.');
      } else {
        message.error(updateResponse?.error || 'Payment successful, but subscription update failed. Please try saving again.');
      }
      // Refresh account data
      const response = await DashboardServiceV4.getOrCreateAccount();
      if (response) {
        creditPriceRate.value = response.credit_price_rate || 0;
        creditPriceRateINR.value = response.credit_price_rate_inr || 0;
        if (response.success) {
          billingAccount.value = response.account;
        }
      }
    } else {
      // Regular checkout (one-time or new subscription setup)
      const response = await DashboardServiceV4.getOrCreateAccount();
      if (response) {
        creditPriceRate.value = response.credit_price_rate || 0;
        creditPriceRateINR.value = response.credit_price_rate_inr || 0;
        if (response.success) {
          billingAccount.value = response.account;
        }
        if (response.auto_recharge_enabled) {
          hasActiveAutoRecharge.value = true;
          monthlyCreditCount.value = response.auto_recharge_credit_count || 20000;
          activeRechargeCreditCount.value = monthlyCreditCount.value;
          monthlyCreditCountDisplay.value = formatNumber(monthlyCreditCount.value);
        }
      }
      if (wasRecurring) {
        hasActiveAutoRecharge.value = true;
        activeRechargeCreditCount.value = monthlyCreditCount.value;
      }
      message.success('Payment successful! Your credits have been added.');
    }
  } catch (error) {
    console.error('Error refreshing billing data:', error);
    message.error('Error updating billing information. Please refresh the page.');
  } finally {
    isProcessingOneTime.value = false;
    isProcessingMonthly.value = false;
  }
};

// Razorpay checkout handlers
const handleRazorpaySuccess = async (paymentData) => {
  showRazorpayCheckout.value = false;
  razorpayOrderData.value = null;
  if (paymentData.type === 'subscription') {
    await handleRazorpaySubscriptionSuccess(paymentData);
  } else {
    await handleRazorpayOrderSuccess(paymentData);
  }
};

const handleRazorpayOrderSuccess = async (paymentData) => {
  const subscriptionUpdateCredits = pendingSubscriptionUpdate.value;
  pendingSubscriptionUpdate.value = null;
  try {
    const verifyResponse = await DashboardServiceV4.verifyRazorpayPayment(paymentData);
    if (verifyResponse?.success) {
      if (subscriptionUpdateCredits) {
        // Delta payment for subscription upgrade — now update the subscription
        const updateResponse = await DashboardServiceV4.updateAutoRecharge(subscriptionUpdateCredits);
        if (updateResponse?.success) {
          hasActiveAutoRecharge.value = true;
          activeRechargeCreditCount.value = subscriptionUpdateCredits;
          monthlyCreditCount.value = subscriptionUpdateCredits;
          monthlyCreditCountDisplay.value = formatNumber(subscriptionUpdateCredits);
          message.success('Payment successful! Your subscription has been updated.');
        } else {
          message.error(updateResponse?.error || 'Payment successful, but subscription update failed. Please try saving again.');
        }
      } else {
        message.success('Payment successful! Your credits have been added.');
      }
      // Refresh account data
      const response = await DashboardServiceV4.getOrCreateAccount();
      if (response) {
        creditPriceRate.value = response.credit_price_rate || 0;
        creditPriceRateINR.value = response.credit_price_rate_inr || 0;
        if (response.success) {
          billingAccount.value = response.account;
        }
      }
    } else {
      message.error('Payment verification failed. Please contact support.');
    }
  } catch (error) {
    console.error('Error verifying Razorpay payment:', error);
    message.error('Error verifying payment. Please contact support.');
  } finally {
    isProcessingOneTime.value = false;
    isProcessingMonthly.value = false;
  }
};

const handleRazorpaySubscriptionSuccess = async (paymentData) => {
  try {
    const response = await DashboardServiceV4.activateRazorpaySubscription({
      razorpay_subscription_id: paymentData.razorpay_subscription_id,
      razorpay_payment_id: paymentData.razorpay_payment_id,
      razorpay_signature: paymentData.razorpay_signature,
      credit_count: monthlyCreditCount.value,
    });
    if (response?.success) {
      hasActiveAutoRecharge.value = true;
      activeRechargeCreditCount.value = monthlyCreditCount.value;
      message.success('Auto-recharge activated! Your credits have been added.');
      // Refresh account data to show updated credit balance
      const accountResponse = await DashboardServiceV4.getOrCreateAccount();
      if (accountResponse) {
        creditPriceRate.value = accountResponse.credit_price_rate || 0;
        creditPriceRateINR.value = accountResponse.credit_price_rate_inr || 0;
        autoRechargePaymentMethod.value = accountResponse.auto_recharge_payment_method || '';
        if (accountResponse.success) {
          billingAccount.value = accountResponse.account;
        }
      }
    } else {
      const subId = paymentData.razorpay_subscription_id;
      const errMsg = response?.error || 'Activation failed';
      message.error(`${errMsg}. Your payment was received — please contact support with subscription ID: ${subId}`);
    }
  } catch (error) {
    console.error('Error activating Razorpay subscription:', error);
    const subId = paymentData.razorpay_subscription_id;
    message.error(`Error activating auto-recharge. Your payment was received — please contact support with subscription ID: ${subId}`);
  } finally {
    isProcessingMonthly.value = false;
  }
};

const handleRazorpayError = (error) => {
  showRazorpayCheckout.value = false;
  razorpayOrderData.value = null;
  isProcessingOneTime.value = false;
  isProcessingMonthly.value = false;
  pendingSubscriptionUpdate.value = null;
  if (error?.message === 'Payment cancelled by user') {
    message.info('Payment cancelled.');
  } else {
    message.error('Payment failed. Please try again.');
  }
};

const handleCheckoutError = (error) => {
  showStripeCheckout.value = false;
  stripeClientSecret.value = '';
  isProcessingOneTime.value = false;
  isProcessingMonthly.value = false;
  pendingSubscriptionUpdate.value = null;

  if (error?.message?.includes('cancel')) {
    message.info('Payment cancelled');
  } else {
    message.error('Payment failed. Please try again.');
  }
  console.error('Checkout error:', error);
};

// Cancel subscription handler
const handleConfirmCancel = async () => {
  isCancelling.value = true;
  try {
    const response = await DashboardServiceV4.cancelAutoRecharge();
    if (response?.success) {
      hasActiveAutoRecharge.value = false;
      activeRechargeCreditCount.value = 0;
      showCancelModal.value = false;
      message.success('Auto-recharge has been cancelled.');
    } else {
      message.error(response?.error || 'Failed to cancel auto-recharge');
    }
  } catch (error) {
    message.error('Error cancelling auto-recharge');
  } finally {
    isCancelling.value = false;
  }
};
</script>

<style scoped>
#manage-billing-v4 {
  font-family: 'Proxima Nova', sans-serif;
}

/* Input styling matching Pricing.vue */
.v4_input_wrapper {
  display: flex;
  align-items: center;
  background-color: #0000000a;
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 0 16px;
  height: 44px;
}

.v4_input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  color: #000;
  font-family: 'Proxima Nova', sans-serif;
}

.v4_input::placeholder {
  color: #666666;
}

.v4_input_suffix {
  color: #666666;
  font-size: 14px;
  margin-left: 8px;
  white-space: nowrap;
}

/* Buy button (solid black) */
.buy_credits_btn {
  background-color: #000 !important;
  color: #fff !important;
  border: none !important;
}

.buy_credits_btn:hover {
  opacity: 0.85;
}

.buy_credits_btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Save changes button (outlined) */
.save_changes_btn {
  background-color: #fff !important;
  color: #000 !important;
  border: 1.5px solid #666 !important;
}

.save_changes_btn:hover {
  background-color: #f5f5f5 !important;
}

.save_changes_btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Quick select chips */
.credit_chip {
  background-color: #0000000a;
  border: 1px solid #00000014;
  color: #000;
  user-select: none;
}

.credit_chip:hover {
  background-color: #00000014;
}

.credit_chip_active {
  background-color: #00000014;
  border-color: #00000029;
}

/* Cancel subscription button (danger outlined) */
.cancel_subscription_btn {
  background-color: #fff !important;
  color: #fa3523 !important;
  border: 1.5px solid #fa3523 !important;
}

.cancel_subscription_btn:hover {
  background-color: rgba(250, 53, 35, 0.06) !important;
}

/* Cancel changes button (secondary outlined) */
.cancel_changes_btn {
  background-color: #fff !important;
  color: #666 !important;
  border: 1.5px solid #ccc !important;
}

.cancel_changes_btn:hover {
  background-color: #f5f5f5 !important;
}

.cancel_changes_btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Section divider */
.section_divider {
  height: 1px;
  background-color: #00000014;
  margin: 0 10%;
}
</style>
